/**
 * Shared types, helpers, and form utilities for Job Description parsed data.
 * Used by JDDetailPage and JobJDPanel.
 */

export interface ParsedJDData {
  title?: string;
  description?: string;
  employment_type?: string;
  experience_level?: string;
  location?: { address?: string; city?: string; country?: string; remote?: boolean };
  salary_range?: {
    text?: string;
    min_amount?: number | string;
    max_amount?: number | string;
    currency?: string;
    period?: string;
  };
  company_info?: { name?: string; description?: string; industry?: string };
  responsibilities?: string[];
  qualifications?: string[];
  skills_required?: string[];
  benefits?: string[];
  tags?: string[];
  error?: { message?: string };
}

export interface ParsedDataForm {
  title: string;
  description: string;
  employment_type: string;
  experience_level: string;
  location_address: string;
  location_city: string;
  location_country: string;
  location_remote: boolean;
  salary_text: string;
  salary_min: string;
  salary_max: string;
  salary_currency: string;
  salary_period: string;
  company_name: string;
  company_description: string;
  company_industry: string;
  responsibilities: string;
  qualifications: string;
  skills_required: string;
  benefits: string;
  tags: string;
}

export function emptyForm(): ParsedDataForm {
  return {
    title: '', description: '', employment_type: '', experience_level: '',
    location_address: '', location_city: '', location_country: '', location_remote: false,
    salary_text: '', salary_min: '', salary_max: '', salary_currency: '', salary_period: '',
    company_name: '', company_description: '', company_industry: '',
    responsibilities: '', qualifications: '', skills_required: '', benefits: '', tags: '',
  };
}

export function parsedToForm(p: ParsedJDData | null): ParsedDataForm {
  const f = emptyForm();
  if (!p) return f;
  f.title = p.title ?? '';
  f.description = p.description ?? '';
  f.employment_type = p.employment_type ?? '';
  f.experience_level = p.experience_level ?? '';
  f.location_address = p.location?.address ?? '';
  f.location_city = p.location?.city ?? '';
  f.location_country = p.location?.country ?? '';
  f.location_remote = p.location?.remote ?? false;
  f.salary_text = p.salary_range?.text ?? '';
  f.salary_min = p.salary_range?.min_amount != null ? String(p.salary_range.min_amount) : '';
  f.salary_max = p.salary_range?.max_amount != null ? String(p.salary_range.max_amount) : '';
  f.salary_currency = p.salary_range?.currency ?? '';
  f.salary_period = p.salary_range?.period ?? '';
  f.company_name = p.company_info?.name ?? '';
  f.company_description = p.company_info?.description ?? '';
  f.company_industry = p.company_info?.industry ?? '';
  f.responsibilities = (p.responsibilities ?? []).join('\n');
  f.qualifications = (p.qualifications ?? []).join('\n');
  f.skills_required = (p.skills_required ?? []).join('\n');
  f.benefits = (p.benefits ?? []).join('\n');
  f.tags = (p.tags ?? []).join('\n');
  return f;
}

export function formToParsed(f: ParsedDataForm): Record<string, unknown> {
  const lines = (s: string) => s.split('\n').map(l => l.trim()).filter(Boolean);
  const result: Record<string, unknown> = {};
  if (f.title) result.title = f.title;
  if (f.description) result.description = f.description;
  if (f.employment_type) result.employment_type = f.employment_type;
  if (f.experience_level) result.experience_level = f.experience_level;
  const loc: Record<string, unknown> = {};
  if (f.location_address) loc.address = f.location_address;
  if (f.location_city) loc.city = f.location_city;
  if (f.location_country) loc.country = f.location_country;
  if (f.location_remote) loc.remote = true;
  if (Object.keys(loc).length) result.location = loc;
  const sal: Record<string, unknown> = {};
  if (f.salary_text) sal.text = f.salary_text;
  if (f.salary_min) sal.min_amount = Number(f.salary_min);
  if (f.salary_max) sal.max_amount = Number(f.salary_max);
  if (f.salary_currency) sal.currency = f.salary_currency;
  if (f.salary_period) sal.period = f.salary_period;
  if (Object.keys(sal).length) result.salary_range = sal;
  const co: Record<string, unknown> = {};
  if (f.company_name) co.name = f.company_name;
  if (f.company_description) co.description = f.company_description;
  if (f.company_industry) co.industry = f.company_industry;
  if (Object.keys(co).length) result.company_info = co;
  const resp = lines(f.responsibilities); if (resp.length) result.responsibilities = resp;
  const qual = lines(f.qualifications); if (qual.length) result.qualifications = qual;
  const skills = lines(f.skills_required); if (skills.length) result.skills_required = skills;
  const ben = lines(f.benefits); if (ben.length) result.benefits = ben;
  const tgs = lines(f.tags); if (tgs.length) result.tags = tgs;
  return result;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function extractFilename(url: string): string {
  return url.split('/').pop() ?? url;
}

export function isPDF(fileTypeOrUrl: string): boolean {
  return fileTypeOrUrl.toLowerCase().includes('pdf');
}
